import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { translations, type Language } from '../i18n/translations';

const RATES_KEY = 'exchange_rates_cache';
const RATES_TTL = 3_600_000;
const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3000/api';
const RATES_API = `${API_BASE_URL}/exchange/rates?from=BRL`;
const PREFS_KEY = 'user_preferences';

function loadCachedRates(currency: string): Record<string, number> | null {
    try {
        const raw = localStorage.getItem(`${RATES_KEY}_${currency}`);
        if (!raw) return null;
        const parsed: { rates: Record<string, number>; ts: number } = JSON.parse(raw);
        if (Date.now() - parsed.ts > RATES_TTL) return null;
        return parsed.rates;
    } catch { return null; }
}

function saveCachedRates(currency: string, rates: Record<string, number>) {
    localStorage.setItem(
        `${RATES_KEY}_${currency}`,
        JSON.stringify({ rates, ts: Date.now() })
    );
}

interface Prefs {
    darkMode: boolean;
    currency: string;
    language: string;
    firstDayOfWeek: string;
}

const defaultPrefs: Prefs = {
    darkMode: false,
    currency: 'BRL',
    language: 'pt-BR',
    firstDayOfWeek: 'sunday',
};

function loadPrefs(): Prefs {
    try {
        const raw = localStorage.getItem(PREFS_KEY);
        return raw ? { ...defaultPrefs, ...JSON.parse(raw) } : defaultPrefs;
    } catch {
        return defaultPrefs;
    }
}

function resolveCurrencySymbol(currency: string, language: string): string {
    try {
        const parts = new Intl.NumberFormat(language, {
            style: 'currency',
            currency,
            currencyDisplay: 'narrowSymbol',
        }).formatToParts(0);
        return parts.find(p => p.type === 'currency')?.value ?? currency;
    } catch {
        return ({ BRL: 'R$', USD: '$', EUR: '€' } as Record<string, string>)[currency] ?? currency;
    }
}

interface PreferencesContextType {
    prefs: Prefs;
    updatePref: <K extends keyof Prefs>(key: K, value: Prefs[K]) => void;
    formatCurrency: (value: number) => string;
    currencySymbol: string;
    ratesLoading: boolean;
    ratesError: boolean;
    t: (key: string, vars?: Record<string, string>) => string;
}
export const PreferencesContext = createContext<PreferencesContextType | undefined>(undefined);

export function PreferencesProvider({ children }: { children: ReactNode }) {
    const [prefs, setPrefs] = useState<Prefs>(loadPrefs);
    const [exchangeRates, setExchangeRates] = useState<Record<string, number>>(() => {
        const cached = loadCachedRates(loadPrefs().currency);
        return cached ?? {};
    });
    const [ratesLoading, setRatesLoading] = useState(false);
    const [ratesError, setRatesError] = useState(false);

    useEffect(() => {
        if (prefs.darkMode) {
            document.documentElement.setAttribute('data-theme', 'dark');
        } else {
            document.documentElement.removeAttribute('data-theme');
        }
    }, []); 

    useEffect(() => {
        if (prefs.currency === 'BRL') return;

        const cached = loadCachedRates(prefs.currency);
        if (cached) {
            const id = setTimeout(() => setExchangeRates(cached), 0);
            return () => clearTimeout(id);
        }

        let cancelled = false;
        setRatesLoading(true);

        fetch(RATES_API)
            .then(r => {
                if (!r.ok) throw new Error('HTTP ' + r.status);
                return r.json();
            })
            .then((data: Record<string, number>) => {
                if (cancelled) return;
                const rates = data ?? {};
                setExchangeRates(rates);
                saveCachedRates(prefs.currency, rates);
                setRatesError(false);
            })
            .catch(() => {
                if (cancelled) return;
                setRatesError(true);
            })
            .finally(() => {
                if (!cancelled) setRatesLoading(false); 
            });

        return () => { cancelled = true; };
    }, [prefs.currency]);

    const updatePref = useCallback(<K extends keyof Prefs>(key: K, value: Prefs[K]) => {
        setPrefs(prev => {
            const next = { ...prev, [key]: value };
            localStorage.setItem(PREFS_KEY, JSON.stringify(next));
            if (key === 'darkMode') {
                if (value) {
                    document.documentElement.setAttribute('data-theme', 'dark');
                } else {
                    document.documentElement.removeAttribute('data-theme');
                }
            }
            return next;
        });
    }, []);

    const contextValue = useMemo(() => {
        const formatCurrency = (value: number) => {
            if (prefs.currency === 'BRL') {
                return new Intl.NumberFormat(prefs.language as Language, {
                    style: 'currency',
                    currency: 'BRL',
                }).format(value);
            }
            const rate = exchangeRates[prefs.currency] ?? null;
            const converted = rate !== null ? value * rate : value;
            const displayCurrency = rate !== null ? prefs.currency : 'BRL';
            return new Intl.NumberFormat(prefs.language as Language, {
                style: 'currency',
                currency: displayCurrency,
            }).format(converted);
        };

        const t = (key: string, vars?: Record<string, string>): string => {
            const lang = prefs.language as Language;
            const dict = translations[lang] ?? translations['pt-BR'];
            let text = dict[key] ?? key;
            if (vars) {
                Object.entries(vars).forEach(([k, v]) => {
                    text = text.replace(`{{${k}}}`, v);
                });
            }
            return text;
        };

        return {
            prefs,
            updatePref,
            formatCurrency,
            currencySymbol: resolveCurrencySymbol(prefs.currency, prefs.language),
            ratesLoading,
            ratesError,
            t,
        };
    }, [prefs, updatePref, exchangeRates, ratesLoading, ratesError]);

    return (
        <PreferencesContext.Provider value={contextValue}>
            {children}
        </PreferencesContext.Provider>
    );
}

export function usePreferences() {
    const ctx = useContext(PreferencesContext);
    if (!ctx) throw new Error('usePreferences must be used within PreferencesProvider');
    return ctx;
}
